import {
  FocusEvent,
  MouseEvent,
  RefObject,
  SyntheticEvent,
  TouchEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { PopperInstance, PopperProps } from '../Popper.types';
import { usePopperPositionUpdate } from './usePopperPositionUpdate';

type UsePopperProps = Pick<PopperProps, 'disabled' | 'disableClickAway' | 'popperProps' | 'onClose'> & {
  toggleRef: RefObject<HTMLElement | null>;
  isOpen: boolean;
  onClosePopper: () => void;
};

export const usePopper = ({
  toggleRef,
  isOpen,
  disabled,
  disableClickAway,
  popperProps,
  onClose,
  onClosePopper,
}: UsePopperProps) => {
  const popperInstanceRef = useRef<PopperInstance | null>(null);
  const popperContentRef = useRef<HTMLDivElement | null>(null);

  const isMouseDownRef = useRef(false);
  const isPopperContentClickedRef = useRef(false);
  const isOpenRef = useRef(isOpen);

  // Automatically update popper position when anchor element moves
  usePopperPositionUpdate({
    anchorRef: toggleRef,
    popperInstanceRef,
    isOpen,
  });

  const isPositionFixed = useMemo(() => document.body.style.position === 'fixed', []);

  const handlePopperInteraction = useCallback(
    (_: SyntheticEvent<HTMLDivElement>, callback: () => void) => {
      if (disabled) {
        return;
      }

      isPopperContentClickedRef.current = true;

      callback();
    },
    [disabled],
  );

  const handlePopperClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      handlePopperInteraction(event, () => {
        popperProps?.onClick?.(event);
      });
    },
    [popperProps?.onClick, handlePopperInteraction],
  );

  const handlePopperTouchEnd = useCallback(
    (event: TouchEvent<HTMLDivElement>) => {
      handlePopperInteraction(event, () => {
        popperProps?.onTouchEnd?.(event);
      });
    },
    [popperProps?.onTouchEnd, handlePopperInteraction],
  );

  const handleClickAway = useCallback(() => {
    if (disabled || disableClickAway || !isOpenRef.current) {
      return;
    }

    if (isPopperContentClickedRef.current) {
      isPopperContentClickedRef.current = false;
      return;
    }

    onClosePopper();
    onClose?.();
  }, [disabled, disableClickAway, onClosePopper, onClose]);

  const handleFocusOut = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      // If it's a mouse click, don't close it (ClickAwayListener will figure it out)
      if (isMouseDownRef.current) {
        return;
      }

      const relatedTarget = event.relatedTarget as HTMLElement | null;

      // If the focus goes inside the popper, don't close it
      if (relatedTarget && popperContentRef.current?.contains(relatedTarget)) {
        return;
      }

      // If the focus moves to toggle, don't close it
      if (relatedTarget && toggleRef.current?.contains(relatedTarget)) {
        return;
      }

      // Close the popper (Tab out)
      onClosePopper();
    },
    [toggleRef, onClosePopper],
  );

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  // Track mousedown/mouseup and touchstart/touchend to distinguish clicks/taps from tabs
  useEffect(() => {
    const handlePointerDown = () => {
      isMouseDownRef.current = true;
    };

    const handlePointerUp = () => {
      requestAnimationFrame(() => {
        isMouseDownRef.current = false;
      });
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('mouseup', handlePointerUp);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('touchend', handlePointerUp);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('mouseup', handlePointerUp);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('touchend', handlePointerUp);
    };
  }, []);

  return {
    popperInstanceRef,
    popperContentRef,
    isPositionFixed,
    handlePopperClick,
    handlePopperTouchEnd,
    handleClickAway,
    handleFocusOut,
  };
};
